import React, { Dispatch, FC, RefObject, useCallback, useEffect, useState } from "react"

import { i18n } from "@/i18n"
import { requestTeamJoined } from "@/services/activity"
import { useGlobalStore } from "@/store/global"
import { CodeInputModal } from "@/Topics/Recall/components/Modals/CodeInputModal"

import styles from "./index.module.scss"

interface SwitcherRef {
    setCurrent: Dispatch<RecordStage>
}

interface EncodeProps {
    switcherRef: RefObject<SwitcherRef | null>
}

type RecordStage = "award" | "encode"

type JoinedTeam = {
    code?: string
    captainUid?: string
    captainNickname?: string | null
} | null

type JoinedTeamRaw = {
    code?: string
    captainUid?: string
    captainNickname?: string | null
    captain?: { uid?: string; nickname?: string | null }
}

const normalizeJoinedTeam = (data: JoinedTeamRaw | null): JoinedTeam => {
    if (!data) return null
    const captainUid = data.captainUid ?? data.captain?.uid
    const captainNickname = data.captainNickname ?? data.captain?.nickname ?? null
    return { code: data.code, captainUid, captainNickname }
}

// Tab 卸载后保留上次结果，避免重进时空白
let _cache: JoinedTeam | undefined = undefined

export const Encode: FC<EncodeProps> = ({ switcherRef }) => {
    const player = useGlobalStore((s) => s.player)
    const validActUser = player?.state.isRecallUser

    // 初始值用缓存（重进 tab 时立即渲染上次结果）
    const [joinedTeam, setJoinedTeam] = useState<JoinedTeam | undefined>(_cache)

    const fetchJoinedTeam = useCallback(async () => {
        try {
            const res = await requestTeamJoined()
            const team =
                res.code === 0
                    ? normalizeJoinedTeam((res.data as JoinedTeamRaw) ?? null)
                    : null
            _cache = team
            setJoinedTeam(team)
        } catch {
            _cache = null
            setJoinedTeam(null)
        }
    }, [])

    useEffect(() => {
        fetchJoinedTeam()
    }, [fetchJoinedTeam])

    const handleClose = () => {
        switcherRef.current?.setCurrent("award")
    }

    // 首次加载中（缓存也没有）
    if (joinedTeam === undefined) return <div className={styles.invite} />

    // 已入队：captainUid 有值即已加入，不依赖 isRecallUser
    if (joinedTeam?.captainUid) {
        return (
            <div className={styles.invite}>
                <div
                    className={styles.already}
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                    dangerouslySetInnerHTML={{
                        __html: i18n.getRecall("record.combine_joinedTeam", {
                            nickname: joinedTeam.captainNickname || "unknown",
                        }),
                    }}
                />
            </div>
        )
    }

    // 未入队 + 是召回用户 → 邀请码输入
    if (validActUser) {
        return (
            <div className={styles.invite}>
                <CodeInputModal
                    open={true}
                    onClose={handleClose}
                    onSuccess={fetchJoinedTeam}
                />
            </div>
        )
    }

    // 未入队 + 非召回用户
    return (
        <div className={styles.invite}>
            <div
                className={styles.notRecall}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                dangerouslySetInnerHTML={{
                    __html: i18n.getRecall("record.not_recall_or_new_player"),
                }}
            />
        </div>
    )
}
